import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { MatBadge } from '@angular/material/badge';
import { MatTooltip } from '@angular/material/tooltip';
import { FilterConfig, IFiltersCDD, IPredefinedFilterSet } from '../models/advanced-search.interface';
import { MatCheckbox } from '@angular/material/checkbox';
import { CommonDropdownComponent } from '../../common-dropdawn/common-dropdown.component';
import { debounceTime, Subject } from 'rxjs';
import { CommonDateRangeComponent } from '../../common-date-range/common-date-range.component';
import {SessionStorageService} from '../../../services/session-storage.service';

@Component({
    selector: 'app-advanced-search',
    standalone: true,
    templateUrl: './advanced-search.component.html',
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        ScrollingModule,
        ReactiveFormsModule,
        MatBadge,
        MatTooltip,
        MatCheckbox,
        CommonDropdownComponent,
        CommonDateRangeComponent,
    ],
    styleUrls: ['./advanced-search.component.scss'],
})
export class AdvancedSearchComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() config: FilterConfig[] = [];
    @Input() showSubmit = false;
    @Input() showSubmitLabel = 'Применить';
    @Input() presets?: IPredefinedFilterSet[] = [];
    @Output() formData: EventEmitter<{ [key: string]: string | null }> = new EventEmitter<{
        [key: string]: string | null;
    }>();
    @Output() scrolledToEnd = new EventEmitter<FilterConfig>();
    @Output() onsumbit = new EventEmitter();
    @ViewChildren(CdkVirtualScrollViewport) viewPorts!: QueryList<CdkVirtualScrollViewport>;
    formValues: { [key: string]: string | null } = {};
    appendMode = new FormControl(false); // Toggle for append mode
    activePresets: Set<string> = new Set(); // Track active presets
    commonDropdownFilters: IFiltersCDD[] = [];
    debounceSubject$ = new Subject<{ [key: string]: string | null }>();
    private previousScrollPosition = 0;

    constructor(
        private cd: ChangeDetectorRef,
        private sessionStorageService: SessionStorageService,
    ) {}

    ngOnInit(): void {
        this.debounceSubject$.pipe(debounceTime(800)).subscribe(formValues => {
            this.formData.emit(formValues);
        });

        this.config.forEach(conf => {
            if (conf.commonDropdownConfig?.formCtrl) {
                this.commonDropdownFilters.push({
                    filterName: conf.name,
                    ctrlDropdown: conf.commonDropdownConfig?.formCtrl,
                });
            }
        });

        if (this.sessionStorageService.getFiltersValues()) {
            this.formValues = this.sessionStorageService.getFiltersValues();

            this.commonDropdownFilters.forEach(filter => {
                filter.ctrlDropdown.setValue(Number(this.formValues[filter.filterName]));
            });
        } else {
            this.setDefaultValues();
        }

        this.cd.detectChanges();

        this.appendMode.valueChanges.subscribe(value => {
            if (!value) {
                this.activePresets.clear();
            }
        });
    }

    ngAfterViewInit(): void {
        let updateRequired = false;
        for (const item of this.config) {
            if (item.defaultOption) {
                this.formValues[item.name] = item.defaultOption;
                updateRequired = true;
            }
        }

        if (updateRequired) {
            this.onSearch();
        }
        this.cd.detectChanges();
    }

    ngOnChanges(): void {
        this.setAutocompleteValues();
    }

    applyCommonDropdownFilter(item: any, event: string | number | null): void {
        const commonDropdownFilter = {
            name: item.name,
            value: event,
        };
        const formValues = this.sessionStorageService.getFiltersValues();
        formValues[commonDropdownFilter.name] = commonDropdownFilter.value ? commonDropdownFilter.value.toString() : null;

        this.sessionStorageService.setFiltersValues(formValues);
        this.onSearch(commonDropdownFilter);
    }

    onSearch(commonDropdownFilter?: { name: string; value: string | number | null }): void {
        const formValues = this.getFormValues();
        const formValuesStorage = this.sessionStorageService.getFiltersValues();
        for (const value in formValues) {
            !formValues[value] && typeof formValues[value] !== 'string' ? (formValues[value] = formValuesStorage?.[value]) : null;
        }

        if (commonDropdownFilter) {
            formValues[commonDropdownFilter.name] = commonDropdownFilter.value ? commonDropdownFilter.value.toString() : null;
        }

        this.sessionStorageService.setFiltersValues(formValues);

        this.debounceSubject$.next(formValues);
    }

    setDefaultValues(): void {
        for (const item of this.config) {
            this.formValues[item.name] = item.defaultOption || null;
        }
        this.sessionStorageService.setFiltersValues(this.formValues);
        this.commonDropdownFilters.forEach(filter => filter.ctrlDropdown.reset());
        this.activePresets.clear();
        this.onSearch();
    }

    setAutocompleteValues(): void {
        for (const item of this.config) {
            this.formValues[item.name] = item.autocompleteValue || null;
        }

        this.appendMode.setValue(false);
        this.activePresets.clear(); // Clear active presets
        this.onSearch();
    }

    getFormValues() {
        return { ...this.formValues };
    }

    onScrollEnd(index: number, item?: any, e?: any) {
        const viewport = this.viewPorts.toArray()[index];
        const scrollPosition = viewport?.measureScrollOffset();

        if (scrollPosition > this.previousScrollPosition) {
            const end = viewport.getRenderedRange().end;
            const total = viewport.getDataLength();

            if (end === total) {
                this.scrolledToEnd.emit(item);
            }

            this.previousScrollPosition = scrollPosition;
        }
    }

    applyPredefinedFilters(preset: IPredefinedFilterSet): void {
        // мультифильтр не работает
        // TODO при необходимости сделать мультифильтр
        if (this.appendMode.value) {
            for (const key in preset.values) {
                if (preset.values[key] !== null) {
                    this.formValues[key] = preset.values[key];
                }
            }
            this.activePresets.add(preset.name);
        } else {
            this.formValues = { ...this.formValues, ...preset.values };
        }

        this.onSearch();
    }

    // принимает с DateRange компонента строку с диапазоном вида "2024-10-31T11:02:00.000Z,2024-11-05T11:02:00.000Z"
    onDateRangeChanged(dateRange: string | null): void {
        if (dateRange) {
            this.config.forEach(item => {
                if (item.type === 'dateRangePicker') {
                    this.formValues[item.name] = dateRange;
                }
            });
        }
        this.onSearch();
        this.cd.detectChanges();
    }

    onSubmit(): void {
        this.onsumbit.emit();
    }
}
